import { useId, useMemo, useState, type KeyboardEvent } from "react";

import { Popover } from "@/components/ui/Popover";
import { filterOptions, moveHighlight, optionAt, type ComboboxOption } from "@/lib/combobox";
import { cx } from "@/lib/cx";

/**
 * Composed from `Popover` + a plain listbox (spec judgement call 4 — Radix
 * has no direct combobox primitive), governed by the WAI-ARIA "combobox
 * with list autocomplete" pattern: DOM focus stays on the text input the
 * whole time; the highlighted option is communicated through
 * `aria-activedescendant`, never by moving focus into the listbox. The
 * keyboard/filter logic itself lives in `lib/combobox.ts`, pure and tested
 * there directly (VC-2.6-06) — this file only wires that logic to markup
 * and to `Popover` for floating-panel positioning and outside-dismiss.
 *
 * Proven only synthetically (judgement call 4): nothing in the app today
 * needs type-to-filter selection, so this component has no real route
 * wiring it in. The discharge record says so plainly rather than
 * manufacturing a consumer.
 */
export interface ComboboxProps {
  label: string;
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

export function Combobox({ label, options, value, onChange, placeholder }: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const listboxId = useId();

  const selectedOption = options.find((option) => option.value === value) ?? null;
  const filtered = useMemo(() => filterOptions(options, query), [options, query]);

  function openList() {
    setOpen(true);
    setHighlighted(0);
  }

  function closeList() {
    setOpen(false);
    setHighlighted(null);
  }

  function commit(option: ComboboxOption) {
    onChange(option.value);
    setQuery("");
    closeList();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setHighlighted((current) => moveHighlight(filtered, current, "next"));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setHighlighted((current) => moveHighlight(filtered, current, "previous"));
    } else if (event.key === "Enter") {
      if (open) {
        event.preventDefault();
        const option = optionAt(filtered, highlighted);
        if (option !== undefined) {
          commit(option);
        }
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        closeList();
      }
    }
  }

  const highlightedOption = optionAt(filtered, highlighted);
  const activeDescendant = highlightedOption ? `${listboxId}-${highlightedOption.value}` : undefined;
  const inputValue = open ? query : (selectedOption?.label ?? "");

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          closeList();
        }
      }}
      preventAutoFocus
      asAnchor
      align="start"
      trigger={
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{label}</span>
          <input
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            {...(activeDescendant !== undefined ? { "aria-activedescendant": activeDescendant } : {})}
            value={inputValue}
            {...(placeholder !== undefined ? { placeholder } : {})}
            onFocus={openList}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              setHighlighted(0);
            }}
            onKeyDown={handleKeyDown}
            className="h-tap-target-min w-full rounded-md border border-border-input bg-surface px-3 text-body text-ink placeholder-ink-tertiary focus:border-border-focus"
          />
        </label>
      }
    >
      <ul id={listboxId} role="listbox" aria-label={label} className="max-h-64 w-full min-w-56 overflow-auto">
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-meta text-ink-secondary">No matches.</li>
        ) : (
          filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${listboxId}-${option.value}`}
              role="option"
              aria-selected={index === highlighted}
              onMouseDown={(event) => {
                // A `mousedown` on the input's own blur target would close
                // the popover (and clear `highlighted`) before the click's
                // own `commit` could read it — `preventDefault` keeps focus
                // on the input throughout the click, matching the "focus
                // never leaves the input" combobox pattern.
                event.preventDefault();
                commit(option);
              }}
              className={cx(
                "cursor-pointer rounded-md px-3 py-2 text-body text-ink",
                index === highlighted ? "bg-surface-hover" : undefined,
              )}
            >
              {option.label}
            </li>
          ))
        )}
      </ul>
    </Popover>
  );
}
